import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import './CitySearch.css';

/**
 * CitySearch Component
 * 
 * Autocomplete search for cities using airport data
 * Reuses airport search API but displays city names
 */
const CitySearch = ({ 
  label = "City", 
  placeholder = "Search city...",
  value,
  onChange,
  required = false,
  className = ""
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [error, setError] = useState(null);
  
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);
  const debounceTimeout = useRef(null);

  // Update display when value prop changes
  useEffect(() => {
    if (value && value.cityCode) {
      // Just show the city code when auto-populated
      // User can search to get full city name
      setSearchTerm(value.cityCode);
    } else if (!value) {
      setSearchTerm('');
    }
  }, [value]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Search cities with debounce
  useEffect(() => {
    if (searchTerm.length < 2) {
      setCities([]);
      setIsOpen(false);
      return;
    }

    // Don't search if the searchTerm looks like a pre-filled value (contains comma)
    // This prevents searching when we auto-populate "LAX" or "Los Angeles, US"
    const isPrefilledFormat = searchTerm.includes(',');
    if (isPrefilledFormat) {
      setCities([]);
      setIsOpen(false);
      return;
    }

    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current);
    }

    debounceTimeout.current = setTimeout(async () => {
      setLoading(true);
      setError(null);

      try {
        // Clean the search term - remove commas, extra spaces
        const cleanKeyword = searchTerm.trim().split(',')[0].trim();
        
        console.log('Searching cities with keyword:', cleanKeyword);
        const response = await axios.get('/api/v1/travel/airports/search', {
          params: { keyword: cleanKeyword }
        });

        console.log('City search response:', response.data);

        if (response.data.success) {
          // Group by city to avoid duplicates
          const uniqueCities = {};
          (response.data.data || []).forEach(airport => {
            const cityKey = `${airport.cityName}-${airport.countryCode}`;
            if (!uniqueCities[cityKey]) {
              uniqueCities[cityKey] = {
                cityName: airport.cityName,
                iataCode: airport.iataCode, // Use first airport as city code
                countryCode: airport.countryCode
              };
            }
          });
          
          const cityList = Object.values(uniqueCities);
          console.log('Unique cities found:', cityList.length);
          setCities(cityList);
          setIsOpen(true);
        } else {
          console.error('Search failed:', response.data.error);
          setError(response.data.error || 'Search failed');
          setCities([]);
        }
      } catch (err) {
        console.error('City search error:', err);
        console.error('Error response:', err.response?.data);
        setError(err.response?.data?.error || err.message || 'Failed to search cities');
        setCities([]);
      } finally {
        setLoading(false);
      }
    }, 300);

    return () => {
      if (debounceTimeout.current) {
        clearTimeout(debounceTimeout.current);
      }
    };
  }, [searchTerm]);

  const handleInputChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    setSelectedIndex(-1);
    
    // Clear the selected city when user types
    if (onChange && value !== searchTerm) {
      onChange(null);
    }
  };

  const handleSelect = (city) => {
    const displayValue = `${city.cityName}, ${city.countryCode}`;
    setSearchTerm(displayValue);
    setIsOpen(false);
    
    if (onChange) {
      onChange({
        cityCode: city.iataCode,
        cityName: city.cityName,
        countryCode: city.countryCode
      });
    }
  };

  const handleKeyDown = (e) => {
    if (!isOpen || cities.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => 
          prev < cities.length - 1 ? prev + 1 : prev
        );
        break;
      
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => prev > 0 ? prev - 1 : -1);
        break;
      
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && cities[selectedIndex]) {
          handleSelect(cities[selectedIndex]);
        }
        break;
      
      case 'Escape':
        setIsOpen(false);
        setSelectedIndex(-1);
        break;
      
      default:
        break;
    }
  };

  return (
    <div className={`city-search ${className}`} ref={wrapperRef}>
      {label && (
        <label className="city-search__label">
          {label}
          {required && <span className="required">*</span>}
        </label>
      )}
      
      <div className="city-search__input-wrapper">
        <input
          ref={inputRef}
          type="text"
          className="city-search__input"
          placeholder={placeholder}
          value={searchTerm}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => searchTerm.length >= 2 && setIsOpen(true)}
          required={required}
          autoComplete="off"
          aria-label={label}
        />
        
        {loading && (
          <div className="city-search__loading">
            <div className="spinner"></div>
          </div>
        )}
        
        {!loading && searchTerm && (
          <button
            type="button"
            className="city-search__clear"
            onClick={() => {
              setSearchTerm('');
              setCities([]);
              setIsOpen(false);
              if (onChange) onChange(null);
              inputRef.current?.focus();
            }}
          >
            ×
          </button>
        )}
      </div>

      {error && <div className="city-search__error">{error}</div>}

      {isOpen && cities.length > 0 && (
        <ul className="city-search__dropdown">
          {cities.map((city, index) => (
            <li
              key={`${city.cityName}-${city.countryCode}`}
              className={`city-search__item ${
                index === selectedIndex ? 'city-search__item--selected' : ''
              }`}
              onClick={() => handleSelect(city)}
              onMouseEnter={() => setSelectedIndex(index)}
            >
              <div className="city-search__item-main">
                <span className="city-search__item-name">
                  {city.cityName}
                </span>
                <span className="city-search__item-country">
                  {city.countryCode}
                </span>
              </div>
            </li>
          ))}
        </ul>
      )}

      {isOpen && searchTerm.length >= 2 && !loading && cities.length === 0 && (
        <div className="city-search__no-results">
          No cities found for "{searchTerm}"
        </div>
      )}
    </div>
  );
};

export default CitySearch;